import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../queries";
import type { Presence } from "../types";

/**
 * Presence: "who is here, and on what" (Phase 5).
 *
 * The client heartbeats a stable per-tab connection id every 30s (half the
 * server's `PRESENCE_TTL_S`), reporting which run it is looking at. The roster is
 * polled on the same cadence; presence is advisory, so a few seconds of staleness
 * is harmless and keeps it off the hot event stream.
 */
const HEARTBEAT_MS = 30_000;
const CONNECTION_KEY = "rl_presence_connection";
const RUN_PATH = /^\/runs\/([0-9a-fA-F-]{36})/;

function connectionId(): string {
  try {
    let id = sessionStorage.getItem(CONNECTION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(CONNECTION_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

const PresenceContext = createContext<Presence[]>([]);

export function usePresenceRoster() {
  return useContext(PresenceContext);
}

/** Distinct actors on the roster, one row each (the newest connection wins). */
export function useOnlinePeople(): Presence[] {
  const roster = usePresenceRoster();
  return useMemo(() => {
    const byActor = new Map<string, Presence>();
    for (const entry of roster) {
      if (!byActor.has(entry.actor)) byActor.set(entry.actor, entry);
    }
    return [...byActor.values()];
  }, [roster]);
}

export function PresenceProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const currentRunId = useMemo(() => {
    const match = location.pathname.match(RUN_PATH);
    return match?.[1] ?? null;
  }, [location.pathname]);

  const presence = useQuery({
    queryKey: ["presence"],
    queryFn: () => api.listPresence(),
    refetchInterval: HEARTBEAT_MS,
  });

  useEffect(() => {
    const id = connectionId();
    const beat = () => {
      void api
        .heartbeatPresence({ connection_id: id, surface: "web", run_id: currentRunId })
        .catch(() => {
          /* presence is best-effort */
        });
    };
    beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [currentRunId]);

  return (
    <PresenceContext.Provider value={presence.data ?? []}>{children}</PresenceContext.Provider>
  );
}
